import React, { memo, useCallback, useMemo } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { ProjectPlanNewRow } from '../../projectPlanNew'

// インラインスタイルで軽量化（styled-componentsのオーバーヘッドを回避）
const numberDivStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
}

// 数値フォーマット関数（コンポーネント外に配置して再生成を防止）
const formatNumber = (v: number): string => {
  return Number.isFinite(v) ? parseFloat(v.toString()).toFixed(2) : ''
}

type ActualHourCellRendererParams = ICellRendererParams<ProjectPlanNewRow>

// カスタム比較関数: 実際に描画に影響する値のみを比較
const arePropsEqual = (
  prevProps: ActualHourCellRendererParams,
  nextProps: ActualHourCellRendererParams
): boolean => {
  // valueが変わったら再レンダリング
  if (prevProps.value !== nextProps.value) return false

  // contextのhoursPerSelectedUnitが変わったら再レンダリング
  const prevRate = prevProps.context?.workloadUnitState?.hoursPerSelectedUnit
  const nextRate = nextProps.context?.workloadUnitState?.hoursPerSelectedUnit
  if (prevRate !== nextRate) return false

  // dataの描画に関係するプロパティを比較
  const prevBody = prevProps.data?.body
  const nextBody = nextProps.data?.body

  // 両方nullish
  if (!prevBody && !nextBody) return true
  // 片方だけnullish
  if (!prevBody || !nextBody) return false

  // wbsItemTypeのisTask()結果に影響するuuid比較
  const prevTypeUuid = prevBody.wbsItem?.wbsItemType?.uuid
  const nextTypeUuid = nextBody.wbsItem?.wbsItemType?.uuid
  if (prevTypeUuid !== nextTypeUuid) return false

  // cumulationの比較（サマリー表示に影響）
  const prevCum = prevBody.cumulation
  const nextCum = nextBody.cumulation
  if (!prevCum && !nextCum) return true
  if (!prevCum || !nextCum) return false

  if (prevCum.countTask !== nextCum.countTask) return false
  if (prevCum.countStatusTaskDiscard !== nextCum.countStatusTaskDiscard) {
    return false
  }
  if (prevCum.sumActualHour !== nextCum.sumActualHour) return false

  return true
}

// React.memoでラップして直接エクスポート
export const ProjectPlanNewActualHourCellRenderer = memo(
  (params: ActualHourCellRendererParams) => {
    const { value, data } = params

    const isClickable = useMemo(
      () =>
        data?.body?.wbsItem.wbsItemType?.isTask() &&
        Number.EPSILON < Math.abs(value),
      [data?.body?.wbsItem.wbsItemType, value]
    )

    const displayValue = useMemo(() => formatNumber(value), [value])

    const summary = useMemo(() => {
      const c = data?.body?.cumulation
      if (
        !c ||
        c.countTask - c.countStatusTaskDiscard <= 0 ||
        !data?.body?.wbsItem?.wbsItemType?.isTask()
      ) {
        return undefined
      }
      const rate: number =
        params.context.workloadUnitState?.hoursPerSelectedUnit || 1
      const sumActualHour = (c.sumActualHour ?? 0) / rate
      return ` / ${formatNumber(sumActualHour / rate)}`
    }, [
      data?.body?.cumulation,
      data?.body?.wbsItem?.wbsItemType,
      params.context.workloadUnitState?.hoursPerSelectedUnit,
    ])

    if (value === undefined) {
      return null
    }

    return (
      <div
        className={'sevend-ag-cell'}
        style={{
          ...numberDivStyle,
          cursor: isClickable ? 'pointer' : undefined,
        }}
      >
        <span style={{ color: isClickable ? 'blue' : undefined }}>
          {displayValue}
        </span>
        {summary && <span>&nbsp;{summary}</span>}
      </div>
    )
  },
  arePropsEqual
)

// displayNameを設定（WDYRでのデバッグ用）
ProjectPlanNewActualHourCellRenderer.displayName =
  'ProjectPlanNewActualHourCellRenderer'
